import { TransmissionSystemOperatorResource } from 'app/_resources/transmission-system-operator.resource';
import { Growl } from 'angular/custom/services/growl.service';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import * as angular from 'angular';

type FlexibleLeadTimeUnitExtended = FlexibleLeadTimeUnit | 'NO_RESTRICTIONS';
type ContractPeriodTimeUnit = 'MONTH' | 'INFINITE';

class OperatorSettingsFcfsLeadTimeConfigurationComponentCtrl
    implements ng.IController
{
    private tsoRefId: number;
    private networkPointId: number;
    public flexibleLeadTimeRule: LeadTimeRuleDto;

    //this is needed to change not immediately the values of the item in the list
    public currentFlexibleLeadTimeRule: LeadTimeRuleDto;
    public flexibleLeadTimeRules: LeadTimeRuleDto[];
    public leadPanelExpanded: boolean = false;
    public labelsUnique: boolean;
    public contractPeriodIsUnique: boolean;
    public contractPeriodIsValid: boolean;
    public minBeforeAndMaxAheadAreValid: boolean;
    public minBeforeIsBiggerAsMaxAhead: boolean;
    public minBeforeAndMaxAheadAreEqual: boolean;

    private onSaveCallback: Function;
    private onCancel: Function;

    //this variables are needed to allow the user to select an infinite as time unit
    public selectedMinBeforeUnit: FlexibleLeadTimeUnitExtended;
    public selectedMaxAheadUnit: FlexibleLeadTimeUnitExtended;
    public selectedContractPeriodUnit: ContractPeriodTimeUnit;

    public flexibleLeadTimeUnitsExtended: FlexibleLeadTimeUnitExtended[] = [
        'MINUTE',
        'HOUR',
        'DAY',
        'WORKING_DAY',
        'MONTH',
        'YEAR',
        'NO_RESTRICTIONS'
    ];
    public contractPeriodTimeUnits: ContractPeriodTimeUnit[] = [
        'MONTH',
        'INFINITE'
    ];

    constructor(
        private TransmissionSystemOperator: TransmissionSystemOperatorResource,
        private growl: Growl,
        private errorHandler: ErrorHandler
    ) {}

    $onInit() {
        this.initialize();
        this.validate();
    }

    public validateLabelIsUnique() {
        //if the current label is the same as the original it is still unique
        if (
            this.currentFlexibleLeadTimeRule.name ===
                this.flexibleLeadTimeRule.name ||
            angular.isUndefined(this.flexibleLeadTimeRules) ||
            this.flexibleLeadTimeRules.length === 0
        ) {
            this.labelsUnique = true;
        } else {
            const numberOfEqualLabels: number =
                this.flexibleLeadTimeRules.filter(
                    (leadTimeRuleDto) =>
                        leadTimeRuleDto.name ==
                        this.currentFlexibleLeadTimeRule.name
                ).length;
            this.labelsUnique = numberOfEqualLabels === 0;
        }
    }

    public validateContractPeriod() {
        this.contractPeriodIsValid =
            this.validateContractPeriodIsSetIfNecessary(
                this.currentFlexibleLeadTimeRule
            );
        this.contractPeriodIsUnique = this.validateContractPeriodIsUnique(
            this.currentFlexibleLeadTimeRule
        );
    }

    private validateContractPeriodIsSetIfNecessary(
        rule: LeadTimeRuleDto
    ): boolean {
        if (
            rule.contractPeriodType === 'NON_WITHINDAY' &&
            this.selectedContractPeriodUnit !== 'INFINITE'
        ) {
            return rule.contractPeriod != null;
        } else {
            return true; // no need for contract period
        }
    }

    private validateContractPeriodIsUnique(rule: LeadTimeRuleDto): boolean {
        return !this.flexibleLeadTimeRules.some(
            (otherRule) =>
                otherRule.id != rule.id &&
                otherRule.contractPeriod == rule.contractPeriod
        );
    }

    public validateMinBeforeAndMaxAhead() {
        this.minBeforeIsBiggerAsMaxAhead = this.isMinBeforeBiggerAsMaxAhead();
        this.minBeforeAndMaxAheadAreEqual = this.areMinBeforeAndMaxAheadEqual();

        if (this.minBeforeOrMaxAheadIsInfiniteAnValid()) {
            this.minBeforeAndMaxAheadAreValid = true;
        } else {
            //  min before and max ahead have to be different
            if (
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMaximumTimeAhead.value ===
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMinimumTimeBefore.value
            ) {
                this.minBeforeAndMaxAheadAreValid = false;
                return;
            }

            //  min before has to be smaller than max ahead
            if (
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMaximumTimeAhead.value <
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMinimumTimeBefore.value
            ) {
                this.minBeforeAndMaxAheadAreValid = false;
                return;
            }

            if (
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMaximumTimeAhead.value == null ||
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMinimumTimeBefore.value == null
            ) {
                this.minBeforeAndMaxAheadAreValid = false;
                return;
            }
            this.minBeforeAndMaxAheadAreValid = true;
        }
    }

    private minBeforeOrMaxAheadIsInfiniteAnValid(): boolean {
        if (
            this.selectedMinBeforeUnit === 'NO_RESTRICTIONS' &&
            this.selectedMaxAheadUnit === 'NO_RESTRICTIONS'
        ) {
            return true;
        }

        if (
            this.selectedMinBeforeUnit === 'NO_RESTRICTIONS' &&
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead
                .value != null
        ) {
            return true;
        }

        if (
            this.selectedMaxAheadUnit === 'NO_RESTRICTIONS' &&
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore
                .value != null
        ) {
            return true;
        }
        return false;
    }

    private validate() {
        this.validateContractPeriod();
        this.validateLabelIsUnique();
        this.validateMinBeforeAndMaxAhead();
    }

    public isValid(): boolean {
        return (
            this.labelsUnique &&
            this.currentFlexibleLeadTimeRule.name !== '' &&
            this.contractPeriodIsValid &&
            this.minBeforeAndMaxAheadAreValid
        );
    }

    private initialize() {
        this.currentFlexibleLeadTimeRule = angular.copy(
            this.flexibleLeadTimeRule
        );

        // if the lead time has no id, it's a new lead time
        if (this.currentFlexibleLeadTimeRule.id != null) {
            if (
                !this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMaximumTimeAhead.value
            ) {
                this.selectedMaxAheadUnit = 'NO_RESTRICTIONS';
                this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead.value =
                    null;
            } else {
                this.selectedMaxAheadUnit =
                    this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead.type;
            }
            if (
                !this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMinimumTimeBefore.value
            ) {
                this.selectedMinBeforeUnit = 'NO_RESTRICTIONS';
                this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore.value =
                    null;
            } else {
                this.selectedMinBeforeUnit =
                    this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore.type;
            }
            if (
                angular.isUndefined(
                    this.currentFlexibleLeadTimeRule.contractPeriod
                )
            ) {
                this.selectedContractPeriodUnit = 'INFINITE';
            } else {
                this.selectedContractPeriodUnit = 'MONTH';
            }
        } else {
            this.selectedMaxAheadUnit =
                this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead.type;
            this.selectedMinBeforeUnit =
                this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore.type;
            this.selectedContractPeriodUnit = 'MONTH';
        }
    }

    public reset() {
        if (this.currentFlexibleLeadTimeRule.id) {
            this.initialize();
            this.validate();
        } else {
            if (!angular.isUndefined(this.onCancel)) {
                this.onCancel();
            }
        }
    }

    // if isMinBefore == false it means, that it is max ahead
    public changePeriodTimeUnit(
        isMinBefore: boolean,
        timeUnit: FlexibleLeadTimeUnitExtended
    ) {
        if (isMinBefore) {
            this.selectedMinBeforeUnit = timeUnit;
            //The time units are only allowed to be different if at least one of them is 'NO_RESTRICTIONS'
            if (
                timeUnit !== 'NO_RESTRICTIONS' &&
                this.selectedMaxAheadUnit !== 'NO_RESTRICTIONS'
            ) {
                this.selectedMaxAheadUnit = timeUnit;
                return;
            }

            if (this.selectedMinBeforeUnit === 'NO_RESTRICTIONS') {
                this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore.value =
                    null;
            }
        } else {
            this.selectedMaxAheadUnit = timeUnit;
            //The time units are only allowed to be different if at least one of them is 'NO_RESTRICTIONS'
            if (
                timeUnit !== 'NO_RESTRICTIONS' &&
                this.selectedMinBeforeUnit !== 'NO_RESTRICTIONS'
            ) {
                this.selectedMinBeforeUnit = timeUnit;
                return;
            }

            if (this.selectedMaxAheadUnit === 'NO_RESTRICTIONS') {
                this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead.value =
                    null;
            }
        }
        this.validateMinBeforeAndMaxAhead();
    }

    public saveLeadTime(form: ng.IFormController) {
        this.validate();
        if (this.isValid() && form.$valid) {
            this.setLeadTimePeriods();
            if (this.networkPointId) {
                this.currentFlexibleLeadTimeRule.networkPointId =
                    this.networkPointId;
            }
            if (this.currentFlexibleLeadTimeRule.id) {
                this.TransmissionSystemOperator.updateFcfsLeadTimeRule(
                    {
                        id: this.tsoRefId,
                        leadTimeId: this.currentFlexibleLeadTimeRule.id
                    },
                    this.currentFlexibleLeadTimeRule,
                    () => {
                        this.doAfterSaving();
                    },
                    (err) => {
                        this.errorHandler(err);
                    }
                );
            } else {
                this.TransmissionSystemOperator.createFcfsLeadTimeRule(
                    { id: this.tsoRefId },
                    this.currentFlexibleLeadTimeRule,
                    (savedLeadtime: LeadTimeRuleDto) => {
                        this.currentFlexibleLeadTimeRule.id = savedLeadtime.id;
                        this.doAfterSaving();
                    },
                    (err) => {
                        this.errorHandler(err);
                    }
                );
            }
        } else {
            let manualErrors: { field: string; message: string }[] = [];
            if (this.minBeforeAndMaxAheadAreEqual) {
                manualErrors.push({
                    field: '',
                    message:
                        'The earliest and the latest time before the start of the runtime must differ'
                });
            }
            if (this.minBeforeIsBiggerAsMaxAhead) {
                manualErrors.push({
                    field: '',
                    message:
                        'The latest time before the start of the runtime must be smaller than the earliest'
                });
            }
            if (
                (!this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMinimumTimeBefore.value ||
                    !this.currentFlexibleLeadTimeRule
                        .leadTimePeriodForMaximumTimeAhead.value) &&
                this.selectedMinBeforeUnit !== 'NO_RESTRICTIONS'
            ) {
                manualErrors.push({
                    field: '',
                    message: 'This field is mandatory'
                });
            }
        }
    }

    changeContractPeriodTimeUnit(timeUnit: ContractPeriodTimeUnit) {
        this.selectedContractPeriodUnit = timeUnit;
        if (timeUnit === 'INFINITE') {
            this.currentFlexibleLeadTimeRule.contractPeriod = null;
        }
        this.validateContractPeriod();
    }

    private doAfterSaving() {
        this.flexibleLeadTimeRule = angular.copy(
            this.currentFlexibleLeadTimeRule
        );

        //update lead time in array if exists; otherwise add new one
        if (
            !angular.isUndefined(
                this.flexibleLeadTimeRules[
                    this.flexibleLeadTimeRules.findIndex(
                        (leadTimeRuleDto) =>
                            leadTimeRuleDto.id === this.flexibleLeadTimeRule.id
                    )
                ]
            )
        ) {
            this.flexibleLeadTimeRules[
                this.flexibleLeadTimeRules.findIndex(
                    (leadTimeRuleDto) =>
                        leadTimeRuleDto.id === this.flexibleLeadTimeRule.id
                )
            ] = this.flexibleLeadTimeRule;
        } else {
            this.flexibleLeadTimeRules.push(this.flexibleLeadTimeRule);
        }
        if (!angular.isUndefined(this.onSaveCallback)) {
            this.onSaveCallback();
        }
        this.growl.show('The lead time was successfully saved', 'success');
    }

    //If the user choose 'NO_RESTRICTIONS' as time unit, the value of the period has to be deleted
    private setLeadTimePeriods() {
        if (this.selectedMinBeforeUnit == 'NO_RESTRICTIONS') {
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore.value =
                null;
        } else {
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore.type =
                this.selectedMinBeforeUnit;
        }
        if (this.selectedMaxAheadUnit == 'NO_RESTRICTIONS') {
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead.value =
                null;
        } else {
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead.type =
                this.selectedMaxAheadUnit;
        }
    }

    private isMinBeforeBiggerAsMaxAhead() {
        return (
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead
                .value <
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMinimumTimeBefore
                .value
        );
    }

    private areMinBeforeAndMaxAheadEqual() {
        return (
            this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead
                .value ===
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMinimumTimeBefore.value &&
            this.selectedMinBeforeUnit !== 'NO_RESTRICTIONS' &&
            this.selectedMaxAheadUnit !== 'NO_RESTRICTIONS' &&
            (this.currentFlexibleLeadTimeRule.leadTimePeriodForMaximumTimeAhead
                .value != null ||
                this.currentFlexibleLeadTimeRule
                    .leadTimePeriodForMinimumTimeBefore.value != null)
        );
    }
}

angular
    .module('app')
    .component('pkpOperatorSettingsFcfsLeadTimeConfiguration', {
        controller: OperatorSettingsFcfsLeadTimeConfigurationComponentCtrl,
        template:
            require('app/operator-settings/operator-settings-fcfs-lead-time-configuration.component.html'),
        bindings: {
            flexibleLeadTimeRule: '=',
            flexibleLeadTimeRules: '=?',
            tsoRefId: '<',
            networkPointId: '<?',
            onSaveCallback: '&?',
            onCancel: '&?'
        }
    });
